import React, { useState, useEffect, useMemo, useRef } from 'react'
import { 
  ClockIcon, 
  CheckIcon, 
  ExclamationTriangleIcon,
  PlayIcon,
  ArrowPathIcon,
  ArrowsPointingOutIcon,
  ArrowsPointingInIcon
} from '@heroicons/react/24/outline'
import { apiService, formatItemName } from '../services/api'

interface KDSStation {
  id: string
  name: string
  description?: string
  color: string
  is_active: boolean
  sort_order: number
  created_at: string
  updated_at: string
}

interface OrderItem {
  id: string
  orderId: string
  orderNumber?: string
  tokenNumber?: number | null
  tableNumber: string
  productName: string
  quantity: number
  variantName?: string
  addons?: any[]
  notes?: string
  status: 'pending' | 'preparing' | 'ready' | 'completed' | 'cancelled' | 'refunded'
  orderTime: Date
  estimatedTime?: number
  actualTime?: number
  station: string
  kdsStationId?: string | null
  bumped_at?: Date | null
}

interface Order {
  id: string
  tableNumber: string
  customerName?: string
  tokenNumber?: number | null
  items: OrderItem[]
  orderTime: Date
  totalAmount: number
  status: 'pending' | 'preparing' | 'ready' | 'completed' | 'cancelled' | 'refunded'
  bumped_at?: Date | null
}

const KDS: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([])
  const [kdsStations, setKdsStations] = useState<KDSStation[]>([])
  const [selectedStation, setSelectedStation] = useState<string>('all')
  const [timers, setTimers] = useState<{ [key: string]: number }>({})
  const [loading, setLoading] = useState(true)
  const [startPreparingMode, setStartPreparingMode] = useState<'item_wise' | 'order_wise'>('item_wise')
  const [currentTime, setCurrentTime] = useState(Date.now())
  const [kdsColorThresholds, setKdsColorThresholds] = useState({
    green: 10, // minutes
    yellow: 15 // minutes
  })
  const [isFullScreen, setIsFullScreen] = useState(false)
  const [kdsPermissions, setKdsPermissions] = useState({
    can_start_order: true,
    can_mark_ready: true,
    can_mark_complete: true
  })
  // New order sound (only when user is on KDS screen); loaded from POS settings
  const [kdsSoundEnabled, setKdsSoundEnabled] = useState(false)
  const [kdsSoundVolume, setKdsSoundVolume] = useState(70)
  const [kdsSoundUrl, setKdsSoundUrl] = useState('')
  const previousOrderIdsRef = useRef<Set<string>>(new Set())
  const firstLoadDoneRef = useRef(false)
  // Refs so the polling interval always sees latest sound settings (avoids stale closure)
  const kdsSoundEnabledRef = useRef(false)
  const kdsSoundVolumeRef = useRef(70)
  const kdsSoundUrlRef = useRef('')
  const latestLoadOrdersRequestRef = useRef(0)
  const lastOptimisticMutationAtRef = useRef(0)
  useEffect(() => {
    kdsSoundEnabledRef.current = kdsSoundEnabled
    kdsSoundVolumeRef.current = kdsSoundVolume
    kdsSoundUrlRef.current = kdsSoundUrl
  }, [kdsSoundEnabled, kdsSoundVolume, kdsSoundUrl])

  const toggleFullScreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen()
        setIsFullScreen(true)
      } else {
        await document.exitFullscreen()
        setIsFullScreen(false)
      }
    } catch (err) {
      console.warn('Fullscreen error:', err)
    }
  }

  useEffect(() => {
    const onFullScreenChange = () => setIsFullScreen(!!document.fullscreenElement)
    document.addEventListener('fullscreenchange', onFullScreenChange)
    return () => document.removeEventListener('fullscreenchange', onFullScreenChange)
  }, [])

  useEffect(() => {
    loadData()
    const interval = setInterval(() => {
      // Update item timers
      setTimers(prev => {
        const updated = { ...prev }
        Object.keys(updated).forEach(key => {
          // Increment timer if it exists (even if it's 0)
          if (updated[key] !== undefined) {
            updated[key] = (updated[key] || 0) + 1
          }
        })
        return updated
      })
      // Update current time for order elapsed time calculation
      setCurrentTime(Date.now())
    }, 1000)
    return () => clearInterval(interval)
  }, [])

  // Auto-refresh orders so new orders appear without manual refresh (poll every 5 seconds)
  useEffect(() => {
    const pollOrders = () => loadOrders()
    const interval = setInterval(pollOrders, 5000)
    return () => clearInterval(interval)
  }, [])

  const loadData = async () => {
    setLoading(true)
    try {
      // Keep critical KDS data blocking, but load auxiliary settings in background.
      await Promise.all([
        loadKDSStations(),
        loadOrders()
      ])

      // Non-critical calls should not delay the KDS board rendering.
      void loadPOSSettings()
      void (async () => {
        try {
          const kdsSettingsRes = await apiService.getMyKdsSettings()
          if (kdsSettingsRes.success && kdsSettingsRes.data) {
            setKdsPermissions({
              can_start_order: kdsSettingsRes.data.can_start_order !== false,
              can_mark_ready: kdsSettingsRes.data.can_mark_ready !== false,
              can_mark_complete: kdsSettingsRes.data.can_mark_complete !== false
            })
          }
        } catch (_) {
          // Not logged in or endpoint failed; keep defaults (all true)
        }
      })()
    } catch (error) {
      console.error('Error loading KDS data:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadPOSSettings = async () => {
    try {
      const response = await apiService.getPOSSettings()
      if (response.success && response.data) {
        const settings = response.data as any
        if (settings.kdsStartPreparingMode) {
          setStartPreparingMode(settings.kdsStartPreparingMode)
        }
        if (settings.kdsColorThresholdGreen !== undefined) {
          setKdsColorThresholds(prev => ({
            ...prev,
            green: settings.kdsColorThresholdGreen
          }))
        }
        if (settings.kdsColorThresholdYellow !== undefined) {
          setKdsColorThresholds(prev => ({
            ...prev,
            yellow: settings.kdsColorThresholdYellow
          }))
        }
        setKdsSoundEnabled(settings.kdsNewOrderSoundEnabled ?? false)
        setKdsSoundVolume(settings.kdsNewOrderSoundVolume ?? 70)
        setKdsSoundUrl(settings.kdsNewOrderSoundUrl ?? '')
      }
    } catch (error) {
      console.error('Error loading POS settings:', error)
    }
  }

  const playNewOrderSound = () => {
    const volume = Math.min(1, Math.max(0, (kdsSoundVolumeRef.current ?? 70) / 100))
    const url = (kdsSoundUrlRef.current ?? '').trim()
    if (url) {
      try {
        const audio = new Audio(url)
        audio.volume = volume
        audio.play().catch(() => { /* ignore autoplay / load errors */ })
      } catch {
        playDefaultBeep(volume)
      }
    } else {
      playDefaultBeep(volume)
    }
  }

  const playDefaultBeep = (volume: number) => {
    try {
      const ctx = new (window.AudioContext || (window as any).webkitAudioContext)()
      const play = () => {
        const osc = ctx.createOscillator()
        const gain = ctx.createGain()
        osc.connect(gain)
        gain.connect(ctx.destination)
        osc.frequency.value = 800
        osc.type = 'sine'
        gain.gain.setValueAtTime(volume, ctx.currentTime)
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.15)
        osc.start(ctx.currentTime)
        osc.stop(ctx.currentTime + 0.15)
      }
      if (ctx.state === 'suspended') {
        ctx.resume().then(play).catch(() => {})
      } else {
        play()
      }
    } catch {
      // ignore
    }
  }

  const loadKDSStations = async () => {
    try {
      const response = await apiService.getKDSStations()
      if (response.success && response.data && Array.isArray(response.data)) {
        const activeStations = response.data.filter((station: KDSStation) => station.is_active)
        setKdsStations(activeStations)
      }
    } catch (error) {
      console.error('Error loading KDS stations:', error)
    }
  }

  const markOptimisticMutation = () => {
    lastOptimisticMutationAtRef.current = Date.now()
  }

  const loadOrders = async (options: { force?: boolean } = {}) => {
    const { force = false } = options
    const requestStartedAt = Date.now()
    const requestId = latestLoadOrdersRequestRef.current + 1
    latestLoadOrdersRequestRef.current = requestId
    try {
      const response = await apiService.getOrders({
        excludeCancelled: true,
        active_only: true,
        date_range: 'today'
      })
      if (response.success && response.data && Array.isArray(response.data)) {
        // Exclude cancelled orders and cancelled items so they are removed from KDS
        const transformedOrders: Order[] = response.data
          .filter((order: any) => order.status !== 'cancelled')
          .map((order: any) => ({
            id: order.id,
            tableNumber: order.table_number || 'Takeaway',
            customerName: order.customer_name,
            tokenNumber: order.token_number || null,
            orderTime: new Date(order.order_time),
            totalAmount: order.total_amount,
            status: order.status,
            bumped_at: order.bumped_at ? new Date(order.bumped_at) : null,
            // Use order_items_for_modification when available so quantities match order details (not summed across KDS stations)
            items: (order.order_items_for_modification || order.order_items || [])
              .filter((item: any) => item.status !== 'cancelled')
              .map((item: any) => ({
                id: item.id,
                orderId: order.id,
                orderNumber: order.order_number,
                tokenNumber: order.token_number || null,
                tableNumber: order.table_number || 'Takeaway',
                productName: formatItemName(item),
                quantity: item.quantity,
                variantName: item.variant_name,
                addons: item.addons || [],
                notes: item.notes,
                status: item.status,
                orderTime: new Date(order.order_time),
                estimatedTime: 15,
                actualTime: undefined,
                station: item.kds_stations?.name || 'Unknown Station',
                kdsStationId: item.kds_station_id || null,
                priority: item.priority || 1,
                created_at: item.created_at,
                updated_at: item.updated_at,
                bumped_at: item.bumped_at ? new Date(item.bumped_at) : null
              }))
          }))
          .filter((order) => order.items.length > 0) // drop orders that have no active items left
        
        const currentOrderIds = new Set(transformedOrders.map((o) => o.id))
        if (!firstLoadDoneRef.current) {
          firstLoadDoneRef.current = true
          previousOrderIdsRef.current = currentOrderIds
        } else {
          const prev = previousOrderIdsRef.current
          const hasNewOrders = transformedOrders.some((o) => !prev.has(o.id))
          previousOrderIdsRef.current = currentOrderIds
          if (
            hasNewOrders &&
            document.visibilityState === 'visible' &&
            kdsSoundEnabledRef.current &&
            (kdsSoundVolumeRef.current ?? 0) > 0
          ) {
            playNewOrderSound()
          }
        }

        const isLatestRequest = requestId === latestLoadOrdersRequestRef.current
        const isStaleVsLocalMutation = requestStartedAt < lastOptimisticMutationAtRef.current
        if (!force && (!isLatestRequest || isStaleVsLocalMutation)) {
          return
        }

        setOrders(transformedOrders)
        
        // Initialize timers for items that are being prepared
        // Timer should start from when status changed to 'preparing' (when Start Order was clicked)
        const initialTimers: { [key: string]: number } = {}
        transformedOrders.forEach(order => {
          order.items.forEach(item => {
            if (item.status === 'preparing') {
              // First, try to get the start time from localStorage (most accurate)
              const storedStartTime = localStorage.getItem(`kds_timer_${item.id}`)
              
              if (storedStartTime) {
                // Use the stored start time to calculate elapsed time
                const startTime = parseInt(storedStartTime, 10)
                initialTimers[item.id] = Math.floor((Date.now() - startTime) / 1000)
              } else {
                // Fallback: use updated_at if available
                const itemUpdatedAt = (item as any).updated_at 
                  ? new Date((item as any).updated_at).getTime()
                  : null
                
                if (itemUpdatedAt) {
                  // Calculate elapsed time from when item status was updated
                  // Store this time in localStorage for future refreshes
                  const elapsed = Math.floor((Date.now() - itemUpdatedAt) / 1000)
                  // Estimate start time and store it
                  const estimatedStartTime = Date.now() - (elapsed * 1000)
                  localStorage.setItem(`kds_timer_${item.id}`, estimatedStartTime.toString())
                  initialTimers[item.id] = elapsed
                } else {
                  // Last resort: start from 0
                  initialTimers[item.id] = 0
                }
              }
            } else {
              // Clean up localStorage for items that are no longer preparing
              localStorage.removeItem(`kds_timer_${item.id}`)
            }
          })
        })
        setTimers(initialTimers)
      }
    } catch (error) {
      console.error('Error loading orders:', error)
      // Keep the current board state on transient fetch failures.
      // Clearing all orders here can blank the KDS UI during brief backend/network hiccups.
    }
  }

  const updateItemStatus = async (itemId: string, status: OrderItem['status']) => {
    // Find the item to get its order ID
    const item = orders.flatMap(o => o.items).find(i => i.id === itemId)
    if (!item) return

    // If starting preparing and mode is order-wise, start all items in the order
    if (status === 'preparing' && startPreparingMode === 'order_wise') {
      const order = orders.find(o => o.id === item.orderId)
      if (order) {
        // Update all pending items in the order
        const pendingItems = order.items.filter(i => i.status === 'pending')
        
        // Update UI optimistically
        markOptimisticMutation()
        setOrders(prevOrders => prevOrders.map(o => 
          o.id === item.orderId 
            ? {
                ...o,
                items: o.items.map(i => 
                  i.status === 'pending' ? { ...i, status: 'preparing' } : i
                )
              }
            : o
        ))

        // Start timers for all items
        const startTime = Date.now()
        const newTimers: { [key: string]: number } = {}
        pendingItems.forEach(i => {
          // Store the start time in localStorage for persistence across refreshes
          localStorage.setItem(`kds_timer_${i.id}`, startTime.toString())
          newTimers[i.id] = 0
        })
        setTimers(prev => ({ ...prev, ...newTimers }))

        // Update all items via API
        try {
          const responses = await Promise.all(
            pendingItems.map(i => apiService.updateOrderItem(i.id, { status: 'preparing' }))
          )
          const failed = responses.filter(r => !r.success)
          if (failed.length > 0) {
            console.error('Failed to update some order items:', failed)
            // Reload to sync state on error
            await loadOrders({ force: true })
          }
          // Don't reload on success - optimistic update is sufficient
        } catch (error) {
          console.error('Error updating order items:', error)
          // Reload to sync state
          await loadOrders({ force: true })
        }
        return
      }
    }

    // Item-wise or other status updates
    markOptimisticMutation()
    setOrders(prevOrders => prevOrders.map(order => ({
      ...order,
      items: order.items.map(item => 
        item.id === itemId ? { ...item, status } : item
      )
    })))

    if (status === 'preparing') {
      // Store the start time in localStorage for persistence across refreshes
      const startTime = Date.now()
      localStorage.setItem(`kds_timer_${itemId}`, startTime.toString())
      setTimers(prev => ({ ...prev, [itemId]: 0 }))
    } else if (status === 'ready' || status === 'completed') {
      // Remove the stored timer when item is no longer preparing
      localStorage.removeItem(`kds_timer_${itemId}`)
      setTimers(prev => {
        const updated = { ...prev }
        delete updated[itemId]
        return updated
      })
    }

    try {
      const response = (status === 'ready' || status === 'completed')
        ? await apiService.updateOrderItemKdsStatusBulk(itemId, status)
        : await apiService.updateOrderItem(itemId, { status })
      if (!response.success) {
        console.error('Failed to update order item status:', response.error)
        if ((response as any).error && String((response as any).error).toLowerCase().includes('permission')) {
          alert((response as any).error || "You don't have permission to perform this action.")
        }
        await loadOrders({ force: true })
      } else if (status === 'ready' || status === 'completed') {
        // Keep parent order status in sync after individual KDS actions.
        await apiService.recalculateOrderStatus(item.orderId)
        await loadOrders({ force: true })
      }
    } catch (error) {
      console.error('Error updating order item status:', error)
      await loadOrders({ force: true })
    }
  }

  const markAllReady = async (orderId: string, items: OrderItem[]) => {
    const preparingItems = items.filter(item => item.status === 'preparing')
    if (preparingItems.length === 0) return

    // Update UI optimistically
    markOptimisticMutation()
    setOrders(prevOrders => prevOrders.map(order => 
      order.id === orderId 
        ? {
            ...order,
            items: order.items.map(item => 
              item.status === 'preparing' ? { ...item, status: 'ready' } : item
            )
          }
        : order
    ))

    // Stop timers for all items
    preparingItems.forEach(item => {
      setTimers(prev => {
        const updated = { ...prev }
        delete updated[item.id]
        return updated
      })
    })

    // Update all matching order rows via API (handles hidden duplicate rows)
    try {
      const response = await apiService.updateOrderKdsStatusBulk(orderId, 'preparing', 'ready', { includePending: true })
      if (!response.success) {
        console.error('Failed bulk update to ready:', response.error)
      } else {
        await apiService.recalculateOrderStatus(orderId)
      }
      // Reload to sync state
      await loadOrders({ force: true })
    } catch (error) {
      console.error('Error updating order items:', error)
      // Reload to sync state
      await loadOrders({ force: true })
    }
  }

  const markAllComplete = async (orderId: string, items: OrderItem[]) => {
    const readyItems = items.filter(item => item.status === 'ready')
    if (readyItems.length === 0) return

    // Update UI optimistically
    markOptimisticMutation()
    setOrders(prevOrders => prevOrders.map(order => 
      order.id === orderId 
        ? {
            ...order,
            items: order.items.map(item => 
              item.status === 'ready' ? { ...item, status: 'completed' } : item
            )
          }
        : order
    ))

    // Update all matching order rows via API (handles hidden duplicate rows)
    try {
      const response = await apiService.updateOrderKdsStatusBulk(orderId, 'ready', 'completed')
      if (!response.success) {
        console.error('Failed bulk update to completed:', response.error)
        // Reload to sync state on error
        await loadOrders({ force: true })
      } else {
        // Recalculate parent order status once after batch completion to avoid
        // race conditions between parallel item updates.
        await apiService.recalculateOrderStatus(orderId)
        // Refresh immediately so parent order status reflects backend recalculation.
        await loadOrders({ force: true })
      }
    } catch (error) {
      console.error('Error updating order items:', error)
      // Reload to sync state on error
      await loadOrders({ force: true })
    }
  }

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const getStationName = (stationId: string | null | undefined) => {
    if (!stationId) return 'Unknown Station'
    const station = kdsStations.find(s => s.id === stationId)
    return station ? station.name : 'Unknown Station'
  }

  // Group orders by order (not individual items)
  // Calculate elapsed time for each order based on when the order was placed
  const getOrderElapsedTime = (order: Order, _items: OrderItem[]) => {
    // Always use order creation time
    const orderTime = order.orderTime.getTime()
    return Math.floor((currentTime - orderTime) / 1000) // seconds
  }

  // Get header color based on order elapsed time
  const getHeaderColor = (order: Order, items: OrderItem[]) => {
    const elapsedSeconds = getOrderElapsedTime(order, items)
    const elapsedMinutes = elapsedSeconds / 60
    
    // Use configurable thresholds
    if (elapsedMinutes < kdsColorThresholds.green) {
      return 'bg-green-500'
    } else if (elapsedMinutes >= kdsColorThresholds.green && elapsedMinutes < kdsColorThresholds.yellow) {
      return 'bg-yellow-600' // Dark yellow
    } else {
      return 'bg-red-500' // Red
    }
  }

  // Group items by priority
  const groupItemsByPriority = (items: OrderItem[]) => {
    const grouped: { [key: number]: OrderItem[] } = {}
    items.forEach(item => {
      const priority = (item as any).priority || 1
      if (!grouped[priority]) {
        grouped[priority] = []
      }
      grouped[priority].push(item)
    })
    return grouped
  }

  // Filter and group orders
  const displayedItems = useMemo(() => {
    const filteredOrders = orders.filter(order => {
      // Exclude cancelled orders
      if (order.status === 'cancelled') return false
      // Filter out orders where all items are completed or cancelled
      const hasActiveItems = order.items.some(item => item.status !== 'completed' && item.status !== 'cancelled')
      if (!hasActiveItems) return false

      if (selectedStation === 'all') {
        return true
      } else {
        // For specific station, include orders that have at least one item for that station
        return order.items.some(item => 
          item.kdsStationId === selectedStation && item.status !== 'completed' && item.status !== 'cancelled'
        )
      }
    })

    // Sort orders by bumped_at, then by order time
    const sortedOrders = filteredOrders.sort((a, b) => {
      const bumpedA = a.bumped_at ? a.bumped_at.getTime() : 0
      const bumpedB = b.bumped_at ? b.bumped_at.getTime() : 0
      if (bumpedA !== bumpedB) {
        return bumpedB - bumpedA // Most recently bumped first
      }
      return a.orderTime.getTime() - b.orderTime.getTime() // Oldest first
    })

    // Return orders with their items (filtered by station if needed); exclude cancelled items
    return sortedOrders.map(order => {
      let orderItems = order.items.filter(item => item.status !== 'completed' && item.status !== 'cancelled')
      
      if (selectedStation !== 'all') {
        orderItems = orderItems.filter(item => item.kdsStationId === selectedStation)
      }

      return {
        order,
        items: orderItems
      }
    }).filter(({ items }) => items.length > 0) // Only include orders with active items
  }, [orders, selectedStation])

  return (
    <div className="h-full w-full bg-gray-50 dark:bg-gray-900 overflow-hidden flex flex-col">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
        <div className="py-3">
          <div className="flex items-center justify-between">
            <div className="pl-4 lg:pl-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Kitchen Display System</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">Monitor and manage kitchen orders</p>
            </div>
            <div className="flex items-center space-x-4 pr-4 lg:pr-6">
              <div className="text-sm text-gray-500 dark:text-gray-400">
                <ClockIcon className="h-4 w-4 inline mr-1" />
                {new Date().toLocaleTimeString()}
              </div>
              <button
                type="button"
                onClick={toggleFullScreen}
                className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
                title={isFullScreen ? 'Exit full screen' : 'Full screen'}
                aria-label={isFullScreen ? 'Exit full screen' : 'Full screen'}
              >
                {isFullScreen ? (
                  <ArrowsPointingInIcon className="h-4 w-4" />
                ) : (
                  <ArrowsPointingOutIcon className="h-4 w-4" />
                )}
              </button>
              <button
                onClick={loadData}
                disabled={loading}
                className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
              >
                <ArrowPathIcon className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                {loading ? 'Loading...' : 'Refresh'}
              </button>
            </div>
          </div>
        </div>

        {/* Station Filter */}
        <div className="pb-3 pl-4 lg:pl-6 pr-4 lg:pr-6">
          <div className="flex space-x-1 bg-gray-200 dark:bg-gray-700 rounded-lg p-1">
            <button
              onClick={() => setSelectedStation('all')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                selectedStation === 'all'
                  ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              All Stations
            </button>
            {kdsStations.map(station => (
              <button
                key={station.id}
                onClick={() => setSelectedStation(station.id)}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  selectedStation === station.id
                    ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
                style={{
                  borderLeft: selectedStation === station.id ? `3px solid ${station.color}` : 'none'
                }}
              >
                {station.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Orders Grid */}
      <div className="flex-1 overflow-y-auto px-4 pt-4">
        {loading ? (
          <div className="text-center py-12">
            <ArrowPathIcon className="h-12 w-12 text-gray-400 mx-auto mb-4 animate-spin" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              Loading KDS stations...
            </h3>
            <p className="text-gray-500 dark:text-gray-400">
              Please wait while we fetch the latest data
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-4">
            {displayedItems.map(({ order, items }) => {
              if (!items || items.length === 0) return null
              
              const firstItem = items[0]
              const orderElapsedTime = getOrderElapsedTime(order, items)
              const completedCount = items.filter(item => item.status === 'ready' || item.status === 'completed').length
              const totalCount = items.length
              const headerColor = getHeaderColor(order, items)
              const itemsByPriority = groupItemsByPriority(items)
              const priorityKeys = Object.keys(itemsByPriority).map(Number).sort((a, b) => a - b)
              
              // Get order number (extract number from token or order number)
              let orderNumber = firstItem.tokenNumber?.toString() || firstItem.orderNumber?.replace(/[^0-9]/g, '') || order.id.slice(-4)
              
              // Get table/location info
              let tableInfo = 'Takeaway'
              if (order.tableNumber && order.tableNumber !== 'Takeaway') {
                // Check if table number already includes location info
                if (order.tableNumber.includes('(')) {
                  tableInfo = `Table: ${order.tableNumber}`
                } else {
                  // Default to Ground Floor if not specified
                  tableInfo = `Table: ${order.tableNumber} (Ground Floor)`
                }
              }
              
              return (
                <div
                  key={order.id}
                  className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden"
                >
                  {/* Colored Header Bar */}
                  <div className={`${headerColor} text-white px-4 py-3`}>
                    <div className="flex items-center justify-between">
                      <div className="flex flex-col">
                        <span className="font-bold text-xl">#{orderNumber}</span>
                        <span className="text-sm opacity-70">
                          {order.orderTime.toLocaleTimeString('en-US', { 
                            hour: '2-digit', 
                            minute: '2-digit',
                            hour12: true 
                          })}
                        </span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-lg font-bold">
                          {completedCount}/{totalCount}
                        </span>
                        <span className="text-sm font-mono">
                          {formatTime(orderElapsedTime)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Blue Table/Location Bar */}
                  <div className="bg-blue-600 dark:bg-blue-700 text-white px-4 py-2">
                    <span className="text-sm font-medium">{tableInfo}</span>
                  </div>

                  {/* Items by Priority */}
                  <div className="p-4 space-y-3">
                    {priorityKeys.map(priority => {
                      const priorityItems = itemsByPriority[priority]
                      return (
                        <div key={priority}>
                          {/* Priority Header */}
                          <div className="bg-gray-700 dark:bg-gray-600 text-white px-3 py-1.5 mb-2 rounded">
                            <span className="text-sm font-medium">Priority {priority}</span>
                          </div>
                          
                          {/* Items in this Priority */}
                          <div className="space-y-1.5">
                            {priorityItems.map(item => {
                              const hasStarted = items.some(i => i.status === 'preparing' || i.status === 'ready')
                              const isPending = item.status === 'pending'
                              const isPreparing = item.status === 'preparing'
                              const isReady = item.status === 'ready'
                              
                              return (
                                <div key={item.id} className="text-sm text-gray-900 dark:text-white">
                                  <div className="flex items-start gap-2">
                                    <span className="text-gray-600 dark:text-gray-400">
                                      {item.quantity} x
                                    </span>
                                    <div className="flex-1">
                                      <div className="flex items-center justify-between gap-2">
                                        <div className="flex items-center gap-2">
                                          <span className="font-medium">{item.productName}</span>
                                          {item.variantName && (
                                            <span className="text-gray-600 dark:text-gray-400">
                                              • {item.variantName}
                                            </span>
                                          )}
                                        </div>
                                        {/* Item Status Badge - Aligned to right */}
                                        {hasStarted && (
                                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                            isPending ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                                            isPreparing ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200' :
                                            isReady ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                                            'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                                          }`}>
                                            {item.status}
                                          </span>
                                        )}
                                      </div>
                                      {item.addons && item.addons.length > 0 && (
                                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                          {Array.isArray(item.addons) 
                                            ? item.addons.map((addon: any) => addon.name || addon).join(', ')
                                            : 'N/A'}
                                        </div>
                                      )}
                                      {item.notes && (
                                        <div className="text-xs text-yellow-600 dark:text-yellow-400 mt-0.5 flex items-center gap-1">
                                          <ExclamationTriangleIcon className="h-3 w-3" />
                                          {item.notes}
                                        </div>
                                      )}
                                      {/* Individual Item Controls - Show when order has started; respect KDS permissions */}
                                      {hasStarted && (
                                        <div className="flex items-center gap-2 mt-2">
                                          {isPending && kdsPermissions.can_start_order && (
                                            <button
                                              onClick={() => updateItemStatus(item.id, 'preparing')}
                                              className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium py-1 px-2 rounded transition-colors flex items-center gap-1"
                                            >
                                              <PlayIcon className="h-3 w-3" />
                                              Start
                                            </button>
                                          )}
                                          {isPreparing && kdsPermissions.can_mark_ready && (
                                            <button
                                              onClick={() => updateItemStatus(item.id, 'ready')}
                                              className="bg-green-600 hover:bg-green-700 text-white text-xs font-medium py-1 px-2 rounded transition-colors flex items-center gap-1"
                                            >
                                              <CheckIcon className="h-3 w-3" />
                                              Ready
                                            </button>
                                          )}
                                          {isReady && kdsPermissions.can_mark_complete && (
                                            <button
                                              onClick={() => updateItemStatus(item.id, 'completed')}
                                              className="bg-gray-600 hover:bg-gray-700 text-white text-xs font-medium py-1 px-2 rounded transition-colors flex items-center gap-1"
                                            >
                                              <CheckIcon className="h-3 w-3" />
                                              Complete
                                            </button>
                                          )}
                                          {/* Timer for preparing items */}
                                          {isPreparing && timers[item.id] !== undefined && (
                                            <span className="text-xs text-gray-600 dark:text-gray-400 font-mono">
                                              {formatTime(timers[item.id])}
                                            </span>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              )
                            })}
                          </div>
                        </div>
                      )
                    })}
                  </div>

                  {/* Action Buttons - respect KDS permissions */}
                  <div className="px-4 pb-4 space-y-2">
                    {items.some(item => item.status === 'pending') && !items.some(item => item.status === 'preparing' || item.status === 'ready') && kdsPermissions.can_start_order ? (
                      <button
                        onClick={() => {
                          const firstPendingItem = items.find(item => item.status === 'pending')
                          if (firstPendingItem) {
                            updateItemStatus(firstPendingItem.id, 'preparing')
                          }
                        }}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium py-2.5 px-4 rounded-md transition-colors flex items-center justify-center"
                      >
                        <PlayIcon className="h-4 w-4 mr-2" />
                        Start Order
                      </button>
                    ) : items.some(item => item.status === 'preparing') && kdsPermissions.can_mark_ready ? (
                      <button
                        onClick={() => markAllReady(order.id, items)}
                        className="w-full bg-green-600 hover:bg-green-700 text-white text-sm font-medium py-2.5 px-4 rounded-md transition-colors flex items-center justify-center"
                      >
                        <CheckIcon className="h-4 w-4 mr-2" />
                        Mark All Ready
                      </button>
                    ) : items.some(item => item.status === 'ready') && items.every(item => item.status === 'ready' || item.status === 'completed') && kdsPermissions.can_mark_complete ? (
                      <button
                        onClick={() => markAllComplete(order.id, items)}
                        className="w-full bg-gray-600 hover:bg-gray-700 text-white text-sm font-medium py-2.5 px-4 rounded-md transition-colors flex items-center justify-center"
                      >
                        <CheckIcon className="h-4 w-4 mr-2" />
                        Mark All Complete
                      </button>
                    ) : items.every(item => item.status === 'completed') ? (
                      // All items are completed
                      <div className="w-full bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-sm font-medium py-2.5 px-4 rounded-md text-center">
                        All items completed
                      </div>
                    ) : null}
                  </div>
                </div>
              )
            })}
          
            {displayedItems.length === 0 && (
              <div className="text-center py-12 col-span-full">
                <ClockIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  No orders for {selectedStation === 'all' ? 'any station' : `${getStationName(selectedStation)} station`}
                </h3>
                <p className="text-gray-500 dark:text-gray-400">
                  New orders will appear here when they come in
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default KDS
